import { rem } from 'polished'
import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'

import LabelGroup from 'LuxKit/Label/LabelGroup'
import BodyText from 'LuxKit/Typography/BodyText'
import { AccommodationPackageOptionTileVisualStyle } from 'components/AccommodationKit/AccommodationSameRoom/AccommodationSameRoomOptionTile'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import { mediaQueryUp } from 'components/utils/breakpoint'
import withOfferPageState from 'contexts/OfferPage/withOfferPageState'
import { preventRenderWithOpenFilter } from 'lib/offer/offerPageUtil'

import PropertyPromotionDetails from './PropertyPromotionDetails'
import PropertyPromotionLabel from './PropertyPromotionLabel'
import PropertyRateInclusions from './PropertyRateInclusions'

export const AnytimeRateOptionTile = styled.div`
  ${AccommodationPackageOptionTileVisualStyle}

  display: grid;
  gap: ${rem(12)} ${rem(32)};
  grid-template-columns: 1fr;
  grid-auto-rows: min-content;

  ${mediaQueryUp.tablet} {
    gap: ${rem(12)} ${rem(16)};
  }

  ${mediaQueryUp.desktop} {
    gap: ${rem(12)} ${rem(32)};
    grid-template-columns: ${rem(238)} 1fr;
  }
`

function isValidInclusion({ minDuration, maxDuration }: App.OfferInclusion, duration: number): boolean {
  return (
    (minDuration == null || duration >= minDuration) &&
    (maxDuration == null || duration <= maxDuration)
  )
}

interface Props {
  property: App.Property.Property
  propertyRoom: App.Property.Room
  duration: number
}

function PropertyAnytimeRateOption({ property, propertyRoom, duration }: Props) {
  const intl = useIntl()
  const [promotion, inclusions, bonusInclusions, luxPlusInclusions, bundleInclusions] = useMemo(() => {
    const { promotion, inclusions, bonusInclusions, luxPlusInclusions, bundleInclusions } = propertyRoom
    return [
      promotion && duration >= promotion.minimumLos ? promotion : undefined,
      inclusions.filter(inclusion => isValidInclusion(inclusion, duration)),
      bonusInclusions.filter(inclusion => isValidInclusion(inclusion, duration)),
      luxPlusInclusions.filter(inclusion => isValidInclusion(inclusion, duration)),
      bundleInclusions.filter(inclusion => isValidInclusion(inclusion, duration)),
    ]
  }, [propertyRoom, duration])

  if (!promotion && !inclusions.length && !bonusInclusions.length && !luxPlusInclusions.length && !bundleInclusions.length) return null

  return <AnytimeRateOptionTile>
    <VerticalSpacer gap={12}>
      <VerticalSpacer gap={4}>
        {promotion && <LabelGroup>
          <PropertyPromotionLabel promotion={promotion} />
        </LabelGroup>}
        <BodyText
          variant="large"
          weight="bold"
          colour="normal-contrast"
        >
          {intl.formatMessage({
            id: 'Q81wyQpQgKNK8UxtYyU6',
            defaultMessage: 'Select dates to unlock package options',
            description: 'Placeholder text when dates aren’t selected for property anytime rate option',
          })}
        </BodyText>
      </VerticalSpacer>
      {promotion && <PropertyPromotionDetails promotion={promotion} />}
    </VerticalSpacer>
    <div>
      <PropertyRateInclusions
        property={property}
        inclusions={inclusions}
        bonusInclusions={bonusInclusions}
        luxPlusInclusions={luxPlusInclusions}
        bundleInclusions={bundleInclusions}
        maxHeight={208}
      />
    </div>
  </AnytimeRateOptionTile>
}

export default withOfferPageState((state) => ({
  property: state.property!, // Guaranteed by `<PropertyContainer/>`.
  duration: state.duration,
  open: !!state.activeFilter,
}), PropertyAnytimeRateOption, preventRenderWithOpenFilter)
